import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Box, useTheme } from '@mui/material';

// Initialize mermaid with default config
mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'basis'
    }
});

/**
 * MermaidDiagram component renders a Mermaid diagram from chart definition text.
 *
 * @param {string} chart - The Mermaid diagram definition
 * @param {string} id - Optional unique ID for the diagram (auto-generated if not provided)
 */
function MermaidDiagram({ chart, id }) {
    const containerRef = useRef(null);
    const theme = useTheme();
    const [diagramId] = useState(() => id || `mermaid-${Math.random().toString(36).substr(2, 9)}`);

    useEffect(() => {
        const renderDiagram = async () => {
            if (containerRef.current && chart) {
                try {
                    // Clear previous content
                    containerRef.current.innerHTML = '';

                    // Update theme based on MUI theme mode
                    const mermaidTheme = theme.palette.mode === 'dark' ? 'dark' : 'default';
                    mermaid.initialize({
                        startOnLoad: false,
                        theme: mermaidTheme,
                        securityLevel: 'loose',
                        flowchart: {
                            useMaxWidth: true,
                            htmlLabels: true,
                            curve: 'basis'
                        }
                    });

                    const { svg } = await mermaid.render(diagramId, chart);
                    containerRef.current.innerHTML = svg;
                } catch (error) {
                    console.error('Mermaid rendering error:', error);
                    containerRef.current.innerHTML = `<pre style="color: red;">Error rendering diagram: ${error.message}</pre>`;
                }
            }
        };

        renderDiagram();
    }, [chart, diagramId, theme.palette.mode]);

    return (
        <Box
            ref={containerRef}
            sx={{
                backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                borderRadius: 1,
                p: 2,
                overflow: 'auto',
                '& svg': {
                    maxWidth: '100%',
                    height: 'auto'
                }
            }}
        />
    );
}

export default MermaidDiagram;
